/**
 * Company-scoping participation store (multi-company WI-4).
 *
 * Holds the tenant's `{ moduleId: enabled }` participation map fetched from
 * `GET /api/v1/tenant/company-scoping`, in a tiny external store so BOTH the
 * non-React axios interceptor (which can't use hooks) and React components read
 * the same value. `useCompanyScopingMap()` feeds the store; `useSyncExternalStore`
 * lets components re-render when it loads/changes.
 *
 * Until the map loads (and for any module the server map omits), participation
 * falls back to `DEFAULT_COMPANY_SCOPED_MODULE_IDS` — the always-on ERP cluster
 * + people, mirroring `shared/config/company_scoping.config.json` `always_on`.
 * This keeps the interceptor + first render behaving exactly as before the map
 * arrives (ERP scoped, everything else not).
 */
import { useSyncExternalStore } from 'react';

/**
 * Always-on fallback set: the ERP cluster + people are structurally
 * company-scoped. Used before the server participation map loads and for any
 * module the map doesn't mention. Keep in sync with the backend config's
 * `always_on` list (`shared/config/company_scoping.config.json`).
 */
export const DEFAULT_COMPANY_SCOPED_MODULE_IDS: ReadonlySet<string> = new Set([
  'accounting',
  'procurement',
  'inventory',
  'sales',
  'shipments',
  'people',
]);

let participationMap: Readonly<Record<string, boolean>> | null = null;
const listeners = new Set<() => void>();

/** Replace the participation map and notify subscribers (called by the hook). */
export function setCompanyScopingMap(next: Record<string, boolean>): void {
  participationMap = Object.freeze({ ...next });
  listeners.forEach((listener) => listener());
}

/** Current map (null until loaded). Mostly for tests / debugging. */
export function getCompanyScopingMap(): Readonly<Record<string, boolean>> | null {
  return participationMap;
}

/** Test-only: reset the store between tests. */
export function resetCompanyScopingMap(): void {
  participationMap = null;
  listeners.forEach((listener) => listener());
}

/**
 * Does `moduleId` participate in the multi-company dimension for this tenant?
 * Reads the loaded map, falling back to the always-on ERP set before it loads
 * or for modules the map omits. Pure + synchronous — safe for the interceptor.
 */
export function isModuleCompanyScoped(moduleId: string): boolean {
  if (participationMap && moduleId in participationMap) {
    return participationMap[moduleId];
  }
  return DEFAULT_COMPANY_SCOPED_MODULE_IDS.has(moduleId);
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

function getSnapshot(): Readonly<Record<string, boolean>> | null {
  return participationMap;
}

/**
 * Subscribe a component to the participation map so it re-renders when the map
 * loads or a toggle changes. Returns the current map (null until loaded).
 */
export function useCompanyScopingMapStore(): Readonly<Record<string, boolean>> | null {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
